//@flow

import capitalize from 'lodash/capitalize';
import Cookies from './cookies';

export const extractEntityId = (str: string): string | null => {
  const idPattern = /([a-f0-9]{12})$/;
  const matchArray = str.match(idPattern);

  return matchArray ? matchArray[0] : null;
};

export const truncateText = (text: string, max: number): string =>
  text.length > max - 3 ? text.substring(0, max - 3) + '...' : text;

export const roundOff = (val: number, dec: number): number =>
  Number(Math.round(val + 'e' + dec) + 'e-' + dec);

export const roundOff2 = (num: number): number => roundOff(num, 2);

export const capitalizeArrElems = (arr: Array<string> | null) =>
  arr ? arr.map(x => capitalize(x)) : null;

export const removeSpaces = (str: string) => str.replace(/\s/g, '');

export const computeDistanceFromTop = args => {
  let element = '',
    x = 0;

  if (!args) throw new Error('Args expected should be string or object');

  // if the id is passed
  if (typeof args === 'string') element = document.getElementById(args);

  //if the refs is passed
  if (typeof args === 'object') element = args;

  while (element) {
    x = x + element.offsetTop;
    element = element.offsetParent;
  }

  return x;
};

export const addEvent = ({ elem, type, eventHandle, options }) => {
  if (elem === null || typeof elem === 'undefined') {
    return;
  }
  if (elem.addEventListener) {
    elem.addEventListener(type, eventHandle, options);
  } else if (elem.attachEvent) {
    elem.attachEvent(`on${type}`, eventHandle);
  } else {
    elem[`on${type}`] = eventHandle;
  }
};

export const removeEvent = ({ elem, type, eventHandle, options }) => {
  if (elem === null || typeof elem === 'undefined') {
    return;
  }
  if (elem.removeEventListener) {
    elem.removeEventListener(type, eventHandle, options);
  } else if (elem.detachEvent) {
    elem.detachEvent(`on${type}`, eventHandle);
  } else {
    elem[`on${type}`] = null;
  }
};

export const isBrowser = () => typeof window !== 'undefined';

export const inProduction = () => process.env.NODE_ENV === 'production';

export const inDevelopment = () => process.env.NODE_ENV === 'development';

export const modifiedIdForNoti = (id, type) => `${id}-${type}`;

export const isCookiePresent = cookie =>
  Boolean(Cookies.prototype.getCookie(cookie));

export const getRandomInt = max => Math.floor(Math.random() * Math.floor(max));

export const schedule = time => Date.now() + time * 1000;

export const connectionTime = (retry, count) => Math.pow(retry, count);

export const appendHoneyGuide = (str: string) =>
  str ? `${str} | HoneyGuide` : '';

export let getLodgeIdFromUrl,
  getTrekIdFromPathname,
  getTrekIdFromUrl,
  isPromptAccepted,
  isSentToServer,
  getUserIdFromUrl,
  getToken,
  getUserIdFromStorage,
  isUserLoggedIn,
  isPushServerConnected,
  getTrekBookingIdFromLocalStorage,
  setTrekBookingIdInLocalStorage,
  removeTrekBookingIdInLocalStorage,
  isSuperUser,
  scrollWindowTo,
  hasTokenExpired,
  isPatron,
  scrollIntoView;

if (isBrowser) {
  hasTokenExpired = () => {
    const expireTime = Number(localStorage.getItem('expireToken')) * 1000;
    const now = Date.now();

    return expireTime ? now > expireTime : false;
  };

  getUserIdFromUrl = () => window.location.pathname.split('/')[1].split('.')[1];

  isSentToServer = () => localStorage.getItem('sentToServer') === '1';

  isPromptAccepted = () => localStorage.getItem('promptNoti');

  getLodgeIdFromUrl = () =>
    window.location.pathname
      .split('/')
      .filter(x => !!x)[0]
      .split('.')[1];

  getTrekIdFromUrl = () =>
    window.location.pathname
      .split('/')
      .filter(x => !!x)[1]
      .split('.')[1];

  getTrekIdFromPathname = pathname =>
    pathname
      .split('/')
      .filter(x => !!x)[1]
      .split('.')[1];

  getToken = () => localStorage.getItem('token');

  getUserIdFromStorage = () => localStorage.getItem('userId');

  isUserLoggedIn = () => Boolean(getToken());

  isPushServerConnected = () => localStorage.getItem('sentToServer') === 1;

  getTrekBookingIdFromLocalStorage = (trekId: string) =>
    localStorage.getItem(trekId);

  setTrekBookingIdInLocalStorage = (trekId: string, trekBookingId) =>
    localStorage.setItem(trekId, trekBookingId);

  removeTrekBookingIdInLocalStorage = (trekId: string) =>
    localStorage.removeItem(trekId);

  isSuperUser = () =>
    localStorage.getItem('roles') &&
    localStorage.getItem('roles').includes('superuser');

  isPatron = () =>
    localStorage.getItem('roles') &&
    localStorage.getItem('roles').includes('patron');

  scrollWindowTo = (elementY, duration) => {
    var startingY = window.pageYOffset;
    var diff = elementY - startingY;
    var start;

    // Bootstrap our animation - it will get called right before next frame shall be rendered.
    window.requestAnimationFrame(function step(timestamp) {
      if (!start) start = timestamp;
      // Elapsed milliseconds since start of scrolling.
      var time = timestamp - start;
      // Get percent of completion in range [0, 1].
      var percent = Math.min(time / duration, 1);

      window.scrollTo(0, startingY + diff * percent);
      // Proceed with animation as long as we wanted it to.
      if (time < duration) {
        window.requestAnimationFrame(step);
      }
    });
  };

  scrollIntoView = elmId => {
    /**
     * Scrolls the window and brings an element into view
     * given element id.
     * @param {string} element id
     */
    if (elmId) {
      // Remove # if it is there
      const id = elmId.replace('#', '');
      const elm = document.getElementById(id);
      if (elm)
        elm.scrollIntoView({
          behavior: 'instant',
          block: 'start',
          inline: 'start',
        });
    }
  };
}

/**
 * Appends creators name before trek tremplate name to create new name or returns as it is
 * @param {string} trekName - Name of trek..
 * @param {string} creatorName - Users Name.
 * @example - getNewTrekName('EBC trek', 'Bear Grylls') => Bear's EBC Trek
 * @example - getNewTrekName('EBC trek', '') => EBC Trek
 */
export const getNewTrekName = (
  trekName: string,
  creatorName: string
): string => {
  const username = creatorName ? creatorName.split(' ')[0] : '';
  if (trekName.toLocaleLowerCase().includes(username.toLocaleLowerCase())) {
    return trekName;
  }
  return username + "'s " + trekName;
};

export const buildQueryParams = (prefix, params, skipParamCheck) => {
  const paramsKeys = Object.keys(params);
  const filteredArray = skipParamCheck
    ? paramsKeys
    : paramsKeys.filter(v => params[v]);
  let result = prefix + '?';

  filteredArray.forEach((v, i) => {
    result += i ? '&' + v + '=' + params[v] : v + '=' + params[v];
  });

  return result;
};

export const isEmptyObject = (obj: Object) => Object.keys(obj).length === 0;

/**
 * Used in Img tags. onError loading original image changes the src attribute to a fallback url
 * @param {browser event object} event
 * @param {string} fallbackImageUrl
 */
export const useFallbackImage = (e: Object, fallbackImageUrl: string) => {
  e.target.onerror = null;
  e.target.src = fallbackImageUrl;
};



// WEBPACK FOOTER //
// ./src/lib/utils.js